import { useEffect, useState } from "react";
import { Undo2, AlertCircle, CheckCircle2, RefreshCw } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { adminListRefunds } from "@/lib/api";

function fmt(cents) {
  return `€${(cents / 100).toFixed(2).replace(".", ",")}`;
}

export default function AdminRefunds() {
  const [refunds, setRefunds] = useState([]);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const d = await adminListRefunds();
      setRefunds(d.refunds || []);
      setTotal(d.total_cents || 0);
      setCount(d.count || 0);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout>
      <div data-testid="admin-refunds-page">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-ink tracking-tight flex items-center gap-3">
              <Undo2 className="h-7 w-7 text-red-600" /> Refunds — Audit Trail
            </h1>
            <p className="text-ink-soft text-sm mt-1">Volledige geschiedenis van alle uitgevoerde refunds. Onmodifieerbaar — alleen kijken.</p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-ink text-sm font-semibold"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Ververs
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl bg-white border border-border p-5">
            <div className="text-xs uppercase tracking-wider font-semibold text-ink-soft">Aantal refunds</div>
            <div className="font-display text-3xl font-bold text-ink mt-2" data-testid="kpi-count">{count}</div>
          </div>
          <div className="rounded-2xl bg-red-50 border-2 border-red-200 p-5">
            <div className="text-xs uppercase tracking-wider font-semibold text-red-700">Totaal terugbetaald</div>
            <div className="font-display text-3xl font-bold text-red-700 mt-2" data-testid="kpi-total">{fmt(total)}</div>
          </div>
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5">
            <div className="text-xs uppercase tracking-wider font-semibold text-amber-700">Handmatig actie vereist</div>
            <div className="font-display text-3xl font-bold text-amber-700 mt-2" data-testid="kpi-manual">
              {refunds.filter((r) => r.status === "manual_required").length}
            </div>
            <div className="text-xs text-amber-600 mt-1">MSP API call faalde — handmatig terugbetalen via MSP dashboard</div>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-ink-soft text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Order ID</th>
                <th className="px-4 py-3 text-left">Bedrag</th>
                <th className="px-4 py-3 text-left">Reden</th>
                <th className="px-4 py-3 text-left">MSP status</th>
                <th className="px-4 py-3 text-left">Door</th>
                <th className="px-4 py-3 text-left">Datum</th>
                <th className="px-4 py-3 text-left">Cleanup</th>
              </tr>
            </thead>
            <tbody>
              {refunds.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-soft">Nog geen refunds uitgevoerd.</td></tr>
              ) : (
                refunds.map((r, i) => (
                  <tr key={`${r.order_id}-${i}`} className="border-t border-border" data-testid={`refund-row-${r.order_id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{r.order_id}</td>
                    <td className="px-4 py-3 font-semibold text-red-700">−{fmt(r.amount_cents || 0)}</td>
                    <td className="px-4 py-3 text-ink-soft text-xs max-w-md truncate" title={r.reason}>{r.reason}</td>
                    <td className="px-4 py-3">
                      {r.status === "processed" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                          <CheckCircle2 className="h-3 w-3" /> Verwerkt
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold" title={r.msp_error || ""}>
                          <AlertCircle className="h-3 w-3" /> Handmatig
                        </span>
                      )}
                      {r.msp_refund_id && <div className="text-[10px] text-ink-soft font-mono mt-0.5">{r.msp_refund_id}</div>}
                    </td>
                    <td className="px-4 py-3 text-ink-soft text-xs">{r.created_by}</td>
                    <td className="px-4 py-3 text-ink-soft text-xs">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3 text-ink-soft text-xs">
                      {r.invalidated_credits > 0 && <div>{r.invalidated_credits} credits</div>}
                      {r.check_refunded && <div>Check gemarkeerd</div>}
                      {r.is_full && !r.invalidated_credits && !r.check_refunded && <div className="text-ink-soft/60">—</div>}
                      {!r.is_full && <div className="text-amber-600">Gedeeltelijk</div>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
